import React from 'react';
import clsx from 'clsx';

import { Icon } from './Icon/Icon';

export enum TagSize {
  small = 'small',
  medium = 'medium'
}

export type TagProps<ComponentType extends React.ElementType> = {
  /**
   * Размер Tag ("small", "medium")
   */
  size: TagSize;
  /**
   * Неактивное состояние Tag
   */
  disabled?: boolean;
  /**
   * Иконка слева Tag (React.ElementType)
   */
  leftIcon?: React.ElementType;
  /**
   * Иконка справа Tag (React.ElementType)
   */
  rightIcon?: React.ElementType;
  /**
   * Класс
   */
  className?: string;
  /**
   * Состояние наведения
   */
  hovered?: boolean;
  /**
   * Состояние фокуса
   */
  focused?: boolean;
  /**
   * Кликабелен ли тег
   */
  clickable?: boolean;
  /**
   * Базовый компонент тега
   */
  component?: ComponentType;
  /**
   * Выбран ли тег
   */
  selected?: boolean;
  /**
   * Произвольные классы элементов:
   * * icon – элемент иконки
   * * iconLeft – элемент левой иконки
   * * iconRight – элемент правой иконки
   * * children – класс контейнера children
   */
  classes?: {
    icon?: string;
    iconLeft?: string;
    iconRight?: string;
    children?: string;
  };
} & Omit<React.ComponentProps<ComponentType>, 'size'>;

export function Tag(props: TagProps<'div'>): JSX.Element;
export function Tag<ComponentType extends React.ElementType>(
  props: TagProps<ComponentType>
): JSX.Element;
export function Tag<ComponentType extends React.ElementType>({
  size = TagSize.small,
  disabled,
  children,
  leftIcon,
  rightIcon,
  className,
  focused,
  hovered,
  clickable,
  selected,
  classes,
  component,
  ...props
}: TagProps<ComponentType>) {
  const Comp = (component || 'div') as React.ElementType;
  return (
    <Comp
      className={clsx('hse-Tag', className, {
        [`hse-Tag_size_${size}`]: size,
        'hse-Tag_disabled': disabled,
        'hse-Tag_focused': focused,
        'hse-Tag_hovered': hovered,
        'hse-Tag_clickable': clickable,
        'hse-Tag_selected': selected
      })}
      {...props}
    >
      {leftIcon && (
        <Icon
          className={clsx(
            'hse-Tag__icon',
            'hse-Tag__icon_left',
            classes?.icon,
            classes?.iconLeft
          )}
          size={Icon.Size.small}
          icon={leftIcon}
        />
      )}
      <span className={classes?.children}>{children}</span>
      {rightIcon && (
        <Icon
          className={clsx(
            'hse-Tag__icon',
            'hse-Tag__icon_right',
            classes?.icon,
            classes?.iconRight
          )}
          size={Icon.Size.small}
          icon={rightIcon}
        />
      )}
    </Comp>
  );
}
Tag.Size = TagSize;
